import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { showToast } from "@/components/ui/show-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useLoginMutation } from "@/services/auth/queries";
import { useAuthStore } from "@/store/auth";
import { usePostHog } from "posthog-js/react";

const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters" })
    .regex(/[A-Za-z]/, { message: "Must contain at least one letter" })
    .regex(/[0-9]/, { message: "Must contain at least one number" })
    .regex(/[^A-Za-z0-9]/, {
      message: "Password must contain at least one special character",
    }),
});

type LoginFormValue = z.infer<typeof formSchema>;

export default function LoginForm() {
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl");
  const navigate = useNavigate();
  const setAuthData = useAuthStore((state) => state.setAuthData);
  const setShowSpotlightModal = useAuthStore(
    (state) => state.setShowSpotlightModal
  );
  const posthog = usePostHog();
  const form = useForm<LoginFormValue>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { mutate, isPending } = useLoginMutation(
    (response) => {
      posthog?.capture("form_submitted", { form_name: "login", success: true });
      setAuthData(response.data.data.token, response.data.data.user);
      setShowSpotlightModal(true);
      showToast("success", "Successfully signed in!");
      navigate(callbackUrl ?? "/dashboard");
    },
    (error) => {
      posthog?.capture("form_submitted", {
        form_name: "login",
        success: false,
      });
      if (error.response.status === 401) {
        showToast(
          "error",
          error.response.data.message || "Invalid email or password"
        );
      } else {
        showToast("error", "Something went wrong!!! Please try again later");
      }
    }
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => mutate(data))}
        className="w-full space-y-4"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-bold">Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter your email..."
                  disabled={isPending}
                  className="h-[50px]"
                  // required
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="relative">
          <Link
            to="/forgot-password"
            className="absolute right-0 pt-0.5 text-sm text-primary"
          >
            Forgot password
          </Link>
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-bold">Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type="password"
                      placeholder="Enter your password..."
                      disabled={isPending}
                      className="h-[50px] pr-10"
                      // required
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          type="submit"
          disabled={isPending}
          className="h-12 w-full text-white"
          aria-label="Sign in"
          trackingEvent="login_submitted"
          trackingProperties={{ location: "login_page" }}
        >
          {isPending ? "Signing in..." : "Continue with Email"}
        </Button>
      </form>
    </Form>
  );
}
